import {
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuBuilding2,
  LuFileText,
  LuMail,
  LuMapPin,
  LuPencil,
  LuPhone,
  LuShield,
  LuStethoscope,
  LuUser,
} from 'react-icons/lu'

import { useGetSpecialties } from '../../../api/api_hooks/specialties'
import { useMyProfile } from '../../../api/api_hooks/user/myProfile'
import { useUpdateProfile } from '../../../api/api_hooks/user/useUpdateProfile'
import { useTranslatedQueryHook } from '../../../utils/useTranslatedQueryHook'
import AvatarBox from '../../AvatarBox'
import GenericDropdown from '../../_shared/GenericDropdown'
import AddressInput from '../components/AddressInput'
import EditableTextField from '../components/EditableTextField'
import OrganizationSearch from '../components/OrganizationSearch'

/**
 * Profile page form — per-field inline edit.
 *
 * Each editable field renders as a read-only pill with a pencil hint;
 * clicking it swaps in an input with ✓ / ✕ actions and PATCHes the
 * single field. No more monolithic "Modifier" submit button — every
 * change is committed independently and refetches the profile so
 * downstream displays (header, sidebar avatar…) stay in sync.
 *
 * Complex fields (organization / address / specialty) keep their
 * native pickers but are wrapped in a "collapsed → expand on click"
 * pattern so the visual language matches the pencil pattern above.
 */
export default function ProfileForm() {
  const { t } = useTranslation()
  const toast = useToast()
  const { data: userData, refetch } = useMyProfile()
  const { mutateAsync } = useUpdateProfile()

  const [orgEditing, setOrgEditing] = useState(false)
  const [addrEditing, setAddrEditing] = useState(false)
  const [specEditing, setSpecEditing] = useState(false)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)

  const useGetSpecialtiesTranslated = () => useTranslatedQueryHook(useGetSpecialties())

  const [firstName, lastName] = useMemo(() => {
    const parts = (userData?.contactName || '').split(' ')
    return [parts[0] || '', parts.slice(1).join(' ') || '']
  }, [userData?.contactName])

  const saveFields = useCallback(
    async (fields: Record<string, string | number | Blob | null | undefined>) => {
      const formData = new FormData()
      Object.entries(fields).forEach(([k, v]) => {
        if (v === undefined) return
        if (v === null) formData.append(k, '')
        else if (v instanceof Blob) formData.append(k, v)
        else formData.append(k, String(v))
      })
      await mutateAsync(formData as unknown as never)
      await refetch()
    },
    [mutateAsync, refetch],
  )

  const notifySuccess = () =>
    toast({
      title: t('shared.common.updatedWithSuccess'),
      status: 'success',
      position: 'top',
      duration: 2500,
      isClosable: true,
    })

  const notifyError = (err: any) =>
    toast({
      title: err?.response?.data?.message || t('shared.errors.errorMsg'),
      status: 'error',
      position: 'top',
      duration: 3500,
      isClosable: true,
    })

  // Avatar is a special case — upload immediately on file pick.
  useEffect(() => {
    if (!avatarFile) return
    let cancelled = false
    saveFields({ file: avatarFile })
      .then(() => {
        if (cancelled) return
        notifySuccess()
        setAvatarFile(null)
      })
      .catch((err) => !cancelled && notifyError(err))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarFile])

  const saveText = async (payload: Record<string, string>) => {
    try {
      await saveFields(payload)
      notifySuccess()
    } catch (err: any) {
      notifyError(err)
      throw err
    }
  }

  const specialtyLabel =
    (userData as any)?.specialty?.name || t('shared.auth.specialtyPlaceholder')

  return (
    <VStack spacing={8} align="stretch">
      {/* Avatar upload */}
      <Flex align="center" gap={4} p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.100">
        <AvatarBox
          avatar={avatarFile || userData?.avatarFilePath}
          register={{
            name: 'file',
            onChange: (async (e: any) => {
              const f = e?.target?.files?.[0]
              if (f) setAvatarFile(f)
            }) as any,
            onBlur: (async () => {}) as any,
            ref: (() => {}) as any,
          }}
          isInvalid={false}
          errorMessage={undefined}
        />
        <Box>
          <Text fontSize="sm" fontWeight="600" color="gray.700">{t('profile.form.photoTitle')}</Text>
          <Text fontSize="xs" color="gray.400" mt={0.5}>{t('profile.form.photoHint')}</Text>
        </Box>
      </Flex>

      {/* General Information Section */}
      <Box>
        <Flex align="center" gap={3} mb={4}>
          <Icon as={LuUser} boxSize={5} color="purple.500" />
          <Heading size="md" color="gray.700">{t('profile.form.generalInfo')}</Heading>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <EditableTextField
            icon={LuUser}
            label={t('shared.auth.firstNameLabel')}
            value={firstName}
            placeholder={t('shared.auth.firstNamePlaceholder')}
            onSave={(v) => saveText({ contactName: `${v} ${lastName}`.trim() })}
          />

          <EditableTextField
            icon={LuUser}
            label={t('shared.auth.lastNameLabel')}
            value={lastName}
            placeholder={t('shared.auth.lastNamePlaceholder')}
            onSave={(v) => saveText({ contactName: `${firstName} ${v}`.trim() })}
          />

          {/* Organization — collapsed display + pencil to open the picker */}
          <Box gridColumn={{ base: 'span 1', md: 'span 2' }}>
            <Flex align="center" gap={2} mb={2}>
              <Icon as={LuBuilding2} boxSize={4} color="gray.500" />
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {t('shared.auth.compagnyNameLabel')}
              </Text>
            </Flex>
            {orgEditing ? (
              <VStack align="stretch" spacing={2}>
                <OrganizationSearch
                  placeholder={t('shared.auth.compagnyNamePlaceholder')}
                  isInvalid={false}
                  errorMessage={undefined}
                  initialValue={userData?.companyName}
                  initialOrganizationId={userData?.organizationId}
                  onOrganizationSelect={(orgId, name) => {
                    saveFields({ organizationId: orgId || '', companyName: name })
                      .then(() => {
                        notifySuccess()
                        setOrgEditing(false)
                      })
                      .catch(notifyError)
                  }}
                />
                <HStack justify="end">
                  <IconButton
                    aria-label="Annuler"
                    icon={<Icon as={LuPencil} />}
                    variant="ghost"
                    size="xs"
                    onClick={() => setOrgEditing(false)}
                  />
                </HStack>
              </VStack>
            ) : (
              <ReadRow
                value={userData?.companyName}
                emptyLabel={t('profile.form.notProvidedF')}
                onClick={() => setOrgEditing(true)}
              />
            )}
          </Box>

          <EditableTextField
            icon={LuPhone}
            label={t('shared.common.phoneLabel')}
            value={userData?.phoneNumber}
            placeholder={t('shared.common.phonePlaceholder')}
            type="tel"
            onSave={(v) => saveText({ phoneNumber: v })}
          />

          <EditableTextField
            icon={LuMail}
            label={t('shared.auth.emailLabel')}
            value={userData?.email}
            disabled
            onSave={async () => {}}
          />
        </SimpleGrid>
      </Box>

      <Divider />

      {/* Address Section */}
      <Box>
        <Flex align="center" gap={3} mb={4}>
          <Icon as={LuMapPin} boxSize={5} color="purple.500" />
          <Heading size="md" color="gray.700">{t('profile.form.workAddress')}</Heading>
        </Flex>
        {addrEditing ? (
          <VStack align="stretch" spacing={2}>
            <AddressInput
              onLocationSelect={(loc, address) => {
                saveFields({
                  fullAddress: address,
                  latitude: loc.lat,
                  longitude: loc.lon,
                })
                  .then(() => {
                    notifySuccess()
                    setAddrEditing(false)
                  })
                  .catch(notifyError)
              }}
              fullAddressOnly
              label={null as any}
              inputData={userData?.fullAddress}
            />
          </VStack>
        ) : (
          <ReadRow
            value={userData?.fullAddress}
            emptyLabel={t('profile.form.addressMissing')}
            onClick={() => setAddrEditing(true)}
          />
        )}
      </Box>

      <Divider />

      {/* Professional Identification */}
      <Box>
        <Flex align="center" gap={3} mb={4}>
          <Icon as={LuShield} boxSize={5} color="purple.500" />
          <Heading size="md" color="gray.700">{t('profile.form.professionalId')}</Heading>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box gridColumn={{ base: 'span 1', md: 'span 2' }}>
            <Flex align="center" gap={2} mb={2}>
              <Icon as={LuStethoscope} boxSize={4} color="gray.500" />
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {t('shared.auth.specialtyLabel')}
              </Text>
            </Flex>
            {specEditing ? (
              <VStack align="stretch" spacing={2}>
                <GenericDropdown
                  value={userData?.specialtyId ?? ''}
                  onChange={(val: any) => {
                    saveFields({ specialtyId: val?.id })
                      .then(() => {
                        notifySuccess()
                        setSpecEditing(false)
                      })
                      .catch(notifyError)
                  }}
                  errorMessage={undefined}
                  useGetHook={useGetSpecialtiesTranslated}
                  label=""
                  placeholder={t('shared.auth.specialtyPlaceholder')}
                />
              </VStack>
            ) : (
              <ReadRow
                value={(userData as any)?.specialty?.name}
                emptyLabel={specialtyLabel}
                onClick={() => setSpecEditing(true)}
              />
            )}
          </Box>

          <EditableTextField
            icon={LuFileText}
            label={t('profile.adeliFinessLabel')}
            value={userData?.adeliFinessNumber}
            placeholder={t('profile.adeliFinessPlaceholder')}
            onSave={(v) => saveText({ adeliFinessNumber: v })}
          />

          <EditableTextField
            icon={LuFileText}
            label={t('profile.rppsLabel')}
            value={userData?.rppsNumber}
            placeholder={t('profile.rppsPlaceholder')}
            onSave={(v) => saveText({ rppsNumber: v })}
          />
        </SimpleGrid>
      </Box>
    </VStack>
  )
}

/** Compact read-mode row for fields backed by a picker (org / address / specialty). */
function ReadRow({
  value,
  emptyLabel,
  onClick,
}: {
  value?: string | null
  emptyLabel: string
  onClick: () => void
}) {
  return (
    <HStack
      spacing={2}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      px={3}
      py={2}
      cursor="text"
      _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
      transition="all 0.15s ease"
      onClick={onClick}
      role="button"
      minH="40px"
    >
      <Text
        flex={1}
        fontSize="sm"
        color={value ? 'gray.800' : 'gray.400'}
        fontStyle={value ? 'normal' : 'italic'}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {value || emptyLabel}
      </Text>
      <Tooltip label="Modifier" hasArrow openDelay={400}>
        <Box>
          <Icon as={LuPencil} boxSize={3.5} color="gray.400" />
        </Box>
      </Tooltip>
    </HStack>
  )
}
